import React, { useEffect, useState } from 'react'
import {
    Typography,
    Stack,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    MenuItem,
    Snackbar,
    Alert,
    Box,
    IconButton,
    CircularProgress
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { AxiosWithAuth } from '../../Utilities/authenticationService'
import { AddOutlined, DeleteOutlined } from '@mui/icons-material'
import ButtonWithConfirmPrompt from '../ButtonWithConfirmPrompt'
import { isMobile } from '../../Utilities/isMobile'
import { InputTime } from '../../Utilities/shiftUtils'

const weekdayOptions = [
    { label: '月曜日', value: 'monday' },
    { label: '火曜日', value: 'tuesday' },
    { label: '水曜日', value: 'wednesday' },
    { label: '木曜日', value: 'thursday' },
    { label: '金曜日', value: 'friday' },
    { label: '土曜日', value: 'saturday' },
    { label: '日曜日', value: 'sunday' }
]

/**
 * Replaces all recurring shifts for an employment with the given rows (bulk delete + recreate).
 * Caller must validate overlaps and field rules before calling.
 */
async function persistRecurringShifts(employmentId, rows) {
    await AxiosWithAuth.delete(`/recurringShiftsDeleteAll/${employmentId}`)
    const posts = []
    rows.forEach(shift => {
        shift.daysArr.forEach(day => {
            const [start_time, end_time] = shift.timeRange.split('-')
            posts.push(
                AxiosWithAuth.post('/recurring_shifts', {
                    employment_id: employmentId,
                    day,
                    start_time,
                    end_time
                })
            )
        })
    })
    if (posts.length) {
        await Promise.all(posts)
    }
}

const parseRecurringResponseToRows = data => {
    const rsArr = []
    data.forEach(shift => {
        const currTimeRange = shift.start_time + '-' + shift.end_time
        let added = false
        rsArr.forEach(s => {
            if (s.timeRange === currTimeRange) {
                s.daysArr.push(shift.day)
                s.dayToId[shift.day] = shift.id
                added = true
            }
        })
        if (!added) {
            rsArr.push({
                timeRange: currTimeRange,
                daysArr: [shift.day],
                dayToId: { [shift.day]: shift.id },
                startError: false,
                endError: false,
                dayError: false
            })
        }
    })
    return rsArr
}

const RecurringShifts = ({
    setShowRecurringShiftModal,
    onRequestClose,
    submitFor,
    viewRecurring = false,
    match,
    modalOpen,
    onDirtyChange,
    setSnackbar,
    history,
    monthLocked = false
}) => {
    const employmentId = submitFor ?? match?.params?.employmentId
    const [dialogue, setDialogue] = useState(false)
    const [editted, setEditted] = useState(false)
    const [recurringShifts, setReccuringShifts] = useState([])
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)
    const [persisting, setPersisting] = useState(false)
    const [deleteLoadingIdx, setDeleteLoadingIdx] = useState(null)

    const reloadFromServer = async () => {
        if (!employmentId) return
        const res = await AxiosWithAuth.get('/recurring_shifts', {
            params: { employment_id: employmentId }
        })
        setReccuringShifts(parseRecurringResponseToRows(res.data))
    }

    useEffect(() => {
        if (modalOpen === false) return
        if (!employmentId) return
        let cancelled = false
        ;(async () => {
            try {
                const res = await AxiosWithAuth.get('/recurring_shifts', {
                    params: { employment_id: employmentId }
                })
                if (!cancelled) {
                    setReccuringShifts(parseRecurringResponseToRows(res.data))
                }
            } catch (e) {
                if (!cancelled) {
                    setSnackbar?.({ message: '固定シフトの読み込みに失敗しました。', severity: 'error' })
                }
            }
        })()
        return () => {
            cancelled = true
        }
    }, [employmentId, modalOpen, setSnackbar])

    useEffect(() => {
        onDirtyChange?.(editted)
    }, [editted, onDirtyChange])

    useEffect(() => {
        if (!history || !editted) return undefined
        const unblock = history.block(() => {
            return '変更された固定シフトが保存されていません。このままページを離れると、変更内容は反映されません。'
        })
        return () => unblock()
    }, [history, editted])

    useEffect(() => {
        if (!editted) return undefined
        const onBeforeUnload = e => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [editted])

    const notifyCalendarRefresh = () => {
        if (viewRecurring && !monthLocked) {
            localStorage.setItem('rsUpdated', true)
        }
    }

    const handleClose = () => {
        notifyCalendarRefresh()
        if (setShowRecurringShiftModal) {
            setShowRecurringShiftModal(false)
        } else {
            history?.goBack()
        }
    }

    const handleDismiss = () => {
        if (onRequestClose) {
            onRequestClose()
        } else if (setShowRecurringShiftModal) {
            setShowRecurringShiftModal(false)
        } else {
            history?.goBack()
        }
    }

    const runPersist = async rows => {
        await persistRecurringShifts(employmentId, rows)
        notifyCalendarRefresh()
        setEditted(false)
    }

    const handleSubmit = async () => {
        setDialogue(false)
        if (hasOverlaps(recurringShifts)) {
            setErrorSnackbarOpen(true)
            return
        }
        if (!handleValidate(recurringShifts, setReccuringShifts)) {
            return
        }
        if (!editted) {
            handleClose()
            return
        }
        setPersisting(true)
        try {
            await runPersist(recurringShifts)
            handleClose()
        } catch (e) {
            setSnackbar?.({ message: '固定シフトの保存に失敗しました。もう一度お試しください。', severity: 'error' })
        } finally {
            setPersisting(false)
        }
    }

    const handleDeleteRowConfirmed = async idx => {
        const row = recurringShifts[idx]
        const next = recurringShifts.filter((_, i) => i !== idx)
        if (hasOverlaps(next)) {
            setErrorSnackbarOpen(true)
            return
        }
        const idsToDelete = Object.values(row.dayToId || {})
        setDeleteLoadingIdx(idx)
        try {
            if (idsToDelete.length > 0) {
                await Promise.all(idsToDelete.map(id => AxiosWithAuth.delete(`/recurring_shifts/${id}`)))
            }
            setReccuringShifts(next)
            notifyCalendarRefresh()
            setSnackbar?.({ message: '固定シフトを削除しました。', severity: 'success' })
        } catch (e) {
            setSnackbar?.({ message: '固定シフトの削除に失敗しました。もう一度お試しください。', severity: 'error' })
            await reloadFromServer()
        } finally {
            setDeleteLoadingIdx(null)
        }
    }

    return (
        <>
            <Stack sx={{ mx: isMobile() ? '.5rem' : 0 }}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant="h6" component="h2" sx={{ textAlign: 'center' }}>
                        固定シフトの設定
                    </Typography>
                    <IconButton onClick={handleDismiss}>
                        <Close />
                    </IconButton>
                </Stack>
                {monthLocked && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        締め切り済み、または提出済みの月はカレンダーへの反映は行われません。テンプレートのみ更新できます。
                    </Typography>
                )}
                {persisting && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {!recurringShifts.length ? (
                    <Typography variant="subtitle1">固定シフトがありません</Typography>
                ) : (
                    recurringShifts.map(({ timeRange, daysArr, startError, endError, dayError }, idx) => (
                        <Stack key={`${timeRange}-${idx}`} spacing={2} sx={{ margin: '1rem 0' }}>
                            {idx > 0 && <Divider orientation="horizontal" flexItem />}
                            <ButtonWithConfirmPrompt
                                component={<DeleteOutlined color="grey" sx={{ fill: 'grey' }} data-testid={`delete-recurring-row-${idx}`} />}
                                title="固定シフトの削除"
                                content="本当に削除しますか？"
                                confirmText="削除する"
                                loading={deleteLoadingIdx === idx || persisting}
                                handleConfirm={() => handleDeleteRowConfirmed(idx)}
                            />
                            <InputTime
                                startValue={timeRange.split('-')[0]}
                                endValue={timeRange.split('-')[1]}
                                startError={startError}
                                endError={endError}
                                startOnChange={e => {
                                    const original = [...recurringShifts]
                                    const rsToEdit = { ...original[idx], dayToId: { ...(original[idx].dayToId || {}) } }
                                    rsToEdit.timeRange = e.target.value + '-' + timeRange.split('-')[1]
                                    original.splice(idx, 1, rsToEdit)
                                    setReccuringShifts(original)
                                    setEditted(true)
                                }}
                                endOnChange={e => {
                                    const original = [...recurringShifts]
                                    const rsToEdit = { ...original[idx], dayToId: { ...(original[idx].dayToId || {}) } }
                                    rsToEdit.timeRange = timeRange.split('-')[0] + '-' + e.target.value
                                    original.splice(idx, 1, rsToEdit)
                                    setReccuringShifts(original)
                                    setEditted(true)
                                }}
                                startTestId="start-time"
                                endTestId="end-time"
                            />
                            <TextField
                                select
                                label="曜日"
                                placeholder="選択してください"
                                SelectProps={{
                                    multiple: true,
                                    value: daysArr || [],
                                    onChange: e => {
                                        const original = [...recurringShifts]
                                        const rsToEdit = { ...original[idx] }
                                        const newDays = e.target.value
                                        const prev = rsToEdit.dayToId || {}
                                        const nd = {}
                                        newDays.forEach(d => {
                                            if (prev[d] != null) nd[d] = prev[d]
                                        })
                                        rsToEdit.daysArr = newDays
                                        rsToEdit.dayToId = nd
                                        original.splice(idx, 1, rsToEdit)
                                        setReccuringShifts(original)
                                        setEditted(true)
                                    }
                                }}
                                sx={{ marginTop: '.5rem' }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ 'data-testid': 'dow' }}
                                error={dayError}
                                helperText={dayError ? '曜日を選択してください。' : null}
                            >
                                {weekdayOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    ))
                )}
            </Stack>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column'
                }}
            >
                <Button
                    onClick={() => {
                        const original = [...recurringShifts]
                        original.push({
                            timeRange: '09:00-17:00',
                            daysArr: [],
                            dayToId: {},
                            startError: false,
                            endError: false,
                            dayError: false
                        })
                        setReccuringShifts(original)
                        setEditted(true)
                    }}
                    variant="outlined"
                    color="paloBlue"
                    className="main-button"
                    style={{ width: '-webkit-fill-available', margin: isMobile() ? '0 1rem 1rem 1rem' : '0 .5rem 1rem 0' }}
                    startIcon={<AddOutlined />}
                    data-testid="add-recurring-shift-btn"
                    disabled={persisting || deleteLoadingIdx !== null}
                >
                    固定シフトの時間帯を追加する
                </Button>
                <Button
                    onClick={() => (handleValidate(recurringShifts, setReccuringShifts) ? setDialogue(true) : null)}
                    variant="contained"
                    color="paloBlue"
                    className="main-button"
                    disabled={!editted || persisting || deleteLoadingIdx !== null}
                    style={{ width: '-webkit-fill-available', margin: isMobile() ? '0 1rem 2rem 1rem' : '0 .5rem 0 0' }}
                >
                    保存する
                </Button>
                <Dialog open={dialogue}>
                    <DialogTitle>固定シフトの登録</DialogTitle>
                    <DialogContent>固定シフトとして登録します。よろしいですか。</DialogContent>
                    <DialogActions>
                        <Button autoFocus onClick={() => setDialogue(false)} disabled={persisting}>
                            キャンセル
                        </Button>
                        <Button onClick={handleSubmit} autoFocus disabled={persisting}>
                            {persisting ? <CircularProgress size={22} /> : '登録する'}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={errorSnackbarOpen}
                    severity="error"
                    onClose={() => setErrorSnackbarOpen(false)}
                >
                    <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error">
                        固定シフトが重複しています
                    </Alert>
                </Snackbar>
            </Box>
        </>
    )
}

export const handleValidate = (recurringShifts, setReccuringShifts) => {
    let issue = false
    recurringShifts.forEach(({ timeRange, daysArr }, idx) => {
        const start_time = timeRange.split('-')[0]
        const end_time = timeRange.split('-')[1]
        if (start_time.split(':')[1] % 15 > 0) {
            const original = [...recurringShifts]
            original[idx].startError = true
            setReccuringShifts(original)
            issue = true
        }
        if (end_time.split(':')[1] % 15 > 0) {
            const original = [...recurringShifts]
            original[idx].endError = true
            setReccuringShifts(original)
            issue = true
        }
        if (daysArr.length === 0) {
            const original = [...recurringShifts]
            original[idx].dayError = true
            setReccuringShifts(original)
            issue = true
        }
    })
    if (issue) {
        return false
    }
    return true
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const timeToMins = time => { const [h, m] = time.split(':').map(Number); return h * 60 + m }

export const hasOverlaps = recurringShifts => {
    const intervals = []
    recurringShifts.forEach(({ timeRange, daysArr }) => {
        const [start_time, end_time] = timeRange.split('-')
        const startMins = timeToMins(start_time)
        const endMins = timeToMins(end_time)
        daysArr.forEach(day => {
            const dayOffset = DAYS.indexOf(day) * 1440
            const shiftStart = dayOffset + startMins
            // overnight shift: end_time < start_time, so it spills into the next day
            const shiftEnd = endMins < startMins
                ? dayOffset + endMins + 1440
                : dayOffset + endMins
            intervals.push([shiftStart, shiftEnd])
        })
    })
    for (let i = 0; i < intervals.length; i++) {
        for (let j = i + 1; j < intervals.length; j++) {
            const [aStart, aEnd] = intervals[i]
            const [bStart, bEnd] = intervals[j]
            if (aStart < bEnd && aEnd > bStart) return true
        }
    }
    return false
}

export default RecurringShifts
